import { Fragment, type ReactNode } from "react"
import { AgentLink } from "@/features/agent-page/agent-link"
import { PersonLink } from "@/features/workspace-knowledge/person-link"
import { cn } from "@/lib/utils"
import { useOptionalProject } from "./project-context"
import type { NamePart, Who } from "./who"

/**
 * UX pass 2026-10-15 (docs/design/ux-pass/README.md §4): one rule for names. Any agent's or person's name, avatar or
 * @mention opens their page: an agent's page in the project (`/agents/<handle>`), a person's page in Knowledge
 * (`/knowledge/people/<id>`). `WhoLink` is the one component for it; `MentionText` links the @handles in plain text.
 */
export type { NamePart, Who } from "./who"
export { whoOfActor, whoOfPrincipal, whoOfRun } from "./who"

/** The quiet link look of a name in text (like a plain link in a sentence): its own colour, a faint underline. */
export const whoLinkClass =
  "tap text-inherit underline decoration-link-line decoration-1 underline-offset-3 hover:decoration-current"

export function WhoLink({
  who,
  children,
  className,
  title,
}: {
  who: Who
  children: ReactNode
  className?: string | undefined
  title?: string | undefined
}) {
  const cls = cn(whoLinkClass, className)
  return who.kind === "agent" ? (
    <AgentLink handle={who.handle} className={cls} title={title}>
      {children}
    </AgentLink>
  ) : (
    <PersonLink userId={who.userId} className={cls} title={title}>
      {children}
    </PersonLink>
  )
}

/**
 * A name in a sentence ("Lea wants to…", "from Lea", "Asked by Becky"): linked when it has a page, else plain text.
 * `plain` (inside a link or a button) never links.
 */
export function NameText({
  part,
  plain = false,
  className,
}: {
  part: NamePart | { readonly name: string; readonly who: Who | null }
  plain?: boolean | undefined
  className?: string | undefined
}) {
  if (!part.who || plain) return className ? <span className={className}>{part.name}</span> : part.name
  return (
    <WhoLink who={part.who} className={className} title={`${part.name}'s page`}>
      {part.name}
    </WhoLink>
  )
}

/** `@handle` not inside a word, an email or a path ("a@b.c", "x/@y" stay text). */
const MENTION = /(^|[^\w@/.])@([a-z0-9][a-z0-9_-]{0,31})/g

/** Text pieces and the handles in it, in order. */
export const splitMentions = (text: string): ReadonlyArray<string | { readonly handle: string }> => {
  const out: Array<string | { readonly handle: string }> = []
  let last = 0
  for (const m of text.matchAll(MENTION)) {
    const start = (m.index ?? 0) + (m[1]?.length ?? 0)
    if (start > last) out.push(text.slice(last, start))
    out.push({ handle: m[2] ?? "" })
    last = start + 1 + (m[2]?.length ?? 0)
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

/**
 * Plain text with its @mentions linked: an agent's handle opens her page, a member's opens their page in Knowledge.
 * Handles the project doesn't know, and any text outside a project, stay text. `plain` (inside a link or a button)
 * never links.
 */
export function MentionText({ text, plain = false }: { text: string; plain?: boolean | undefined }) {
  const ctx = useOptionalProject()
  if (!ctx || plain || !text.includes("@")) return <>{text}</>
  const pieces = splitMentions(text)
  return (
    <>
      {pieces.map((p, i) => {
        if (typeof p === "string")
          // biome-ignore lint/suspicious/noArrayIndexKey: pieces of one string, in order
          return <Fragment key={i}>{p}</Fragment>
        const agent = ctx.state.agents.find((a) => a.handle === p.handle)
        const member = agent ? undefined : ctx.state.members.find((m) => m.handle === p.handle)
        const who: Who | null = agent
          ? { kind: "agent", handle: agent.handle }
          : member
            ? { kind: "person", userId: member.userId }
            : null
        return who ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: the same handle may appear twice
          <WhoLink key={i} who={who} title={`${agent?.name ?? member?.displayName}'s page`}>
            @{p.handle}
          </WhoLink>
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: pieces of one string, in order
          <Fragment key={i}>@{p.handle}</Fragment>
        )
      })}
    </>
  )
}
